import React, { useState, useMemo, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Autocomplete, CircularProgress, Typography, Box, Avatar, Stack, Divider
} from '@mui/material';
import { debounce } from 'lodash';
import { modalSurfaceSx } from '../styles/uiTokens';

function RegistrarEntradaModal({ open, onClose, acessoLiberado, onRegistered }) {
  const { user } = useAuth();
  const [veiculoOptions, setVeiculoOptions] = useState([]);
  const [selectedVeiculo, setSelectedVeiculo] = useState(null);
  const [loadingVeiculos, setLoadingVeiculos] = useState(false);
  const [observacoes, setObservacoes] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const debouncedFetchVeiculos = useMemo(() => debounce((searchTerm) => {
    setLoadingVeiculos(true);
    api.get(`/veiculos?search=${encodeURIComponent(searchTerm)}`)
      .then((response) => setVeiculoOptions(response.data.data || response.data))
      .catch((error) => console.error('Erro ao buscar veiculos:', error))
      .finally(() => setLoadingVeiculos(false));
  }, 500), []);

  useEffect(() => () => debouncedFetchVeiculos.cancel(), [debouncedFetchVeiculos]);

  useEffect(() => {
    if (open) {
      setSelectedVeiculo(null);
      setVeiculoOptions([]);
      setObservacoes('');
      setErrorMsg('');
    }
  }, [open, acessoLiberado]);

  const handleConfirm = () => {
    if (!acessoLiberado) return;
    setSaving(true);
    setErrorMsg('');

    const payload = {
      empresa_id: user.tipo_usuario === 'admin' ? acessoLiberado.empresa_id : user.empresa_id,
      imovel_id: acessoLiberado.imovel_id,
      pessoa_id: acessoLiberado.pessoa_id,
      autorizador_id: acessoLiberado.autorizador_id || null,
      acesso_liberado_id: acessoLiberado.id,
      funcionario_entrada_id: user.pessoa_id || null,
      veiculo_id: selectedVeiculo ? selectedVeiculo.id : null,
      observacoes: observacoes || null,
    };

    api.post('/acessos', payload)
      .then(() => {
        onRegistered();
        onClose();
      })
      .catch((error) => {
        console.error('Erro ao registrar entrada:', error);
        const msg = error?.response?.data?.message
          || (typeof error?.response?.data === 'string' ? error.response.data : null)
          || 'Erro ao registrar entrada.';
        setErrorMsg(msg);
      })
      .finally(() => setSaving(false));
  };

  const documento = acessoLiberado
    ? (acessoLiberado.pessoa_cpf || acessoLiberado.pessoa_rg || 'Não informado')
    : '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: modalSurfaceSx }}>
      <DialogTitle>Registrar Entrada — Acesso Liberado</DialogTitle>
      <DialogContent dividers>
        {acessoLiberado && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar
              src={acessoLiberado.foto_base64 || undefined}
              sx={{ width: 64, height: 64 }}
            >
              {(acessoLiberado.pessoa_nome || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6">{acessoLiberado.pessoa_nome}</Typography>
              <Typography variant="body2" color="text.secondary">
                Documento: {documento}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unidade: {acessoLiberado.imovel_numero ? `Unid. ${acessoLiberado.imovel_numero}` : 'N/A'}
                {acessoLiberado.autorizador_nome ? ` • Autorizador: ${acessoLiberado.autorizador_nome}` : ''}
              </Typography>
            </Box>
          </Stack>
        )}

        <Divider sx={{ mb: 2 }} />

        <Autocomplete
          options={veiculoOptions}
          getOptionLabel={(option) => `${option.placa} (${option.marca_nome || option.marca || ''} ${option.modelo || ''})`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedVeiculo}
          onChange={(event, newValue) => setSelectedVeiculo(newValue)}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchVeiculos(newInputValue);
              else setVeiculoOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Veículo (opcional)"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingVeiculos ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          label="Observações (opcional)"
          variant="outlined"
          fullWidth
          multiline
          rows={2}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          margin="normal"
        />

        {errorMsg && <Typography color="error" sx={{ mt: 1 }}>{errorMsg}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={saving}>Cancelar</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" disabled={saving}>
          {saving ? 'Registrando...' : 'Confirmar Entrada'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default RegistrarEntradaModal;
